import React, { useState, useEffect } from 'react';
import {
  Container,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Box,
  TextField,
  InputAdornment,
  Chip,
  Button,
  Grid,
  Avatar,
  Fab,
  Tooltip,
  TablePagination,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import {
  Search as SearchIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  People as PeopleIcon,
  PersonAdd as PersonAddIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  LocationOn as LocationIcon,
  FilterList as FilterListIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useSelector, useDispatch } from 'react-redux';
import { setCustomers } from '../../../app/store/store';
import { getCustomers, deleteCustomer } from '../services/customerService';
import { useTranslation } from "react-i18next";

const CustomersListPage = () => {
  const dispatch = useDispatch();
  const data = useSelector(state => state.customer.customers);
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(() => {
    fetchCustomers();
  }, []);

  const fetchCustomers = async () => {
    try {
      setLoading(true);
      const response = await getCustomers();
      const customersData = response?.data?.payload || response?.payload || response?.data || response || [];
      setCustomers(customersData);
    } catch (error) {
      toast.error(t("error_customer_action"));
      setCustomers([]);
    } finally {
      setLoading(false);
    }
  };

  const handleRemove = async (id) => {
    setLoading(true);
    try {
      await deleteCustomer(id);
      dispatch(setCustomers(data.filter(item => item.Id !== id)));
    } catch (error) {
      toast.error(t("error_customer_action"));
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (customer, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    navigate(`/customers/edit/${customer.Id}`, {
      state: { customerData: customer }
    });
  };

  const handleDelete = async (customerId, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    const customerToDelete = customers.find(c => c.Id === customerId);
    const customerName = customerToDelete?.CustomerName || customerToDelete?.Name || t("customer");
    if (window.confirm(t("confirm_delete_customer", { customer: customerName }))) {
      try {
        await deleteCustomer(customerId);
        toast.success(t("customer_deleted_success"));
        fetchCustomers();
      } catch (error) {
        toast.error(t("error_customer_action"));
      }
    }
  };

  const handleView = (customer, event) => {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    navigate(`/customers/detail/${customer.Id}`, {
      state: { customerData: customer }
    });
  };

  const filteredCustomers = customers.filter(customer => {
    const customerName = customer.CustomerName || customer.Name || '';
    const customerEmail = customer.Email || '';
    const customerPhone = customer.PhoneNumber || customer.Phone || '';
    const matchesSearch =
      customerName.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerEmail.toLowerCase().includes(searchTerm.toLowerCase()) ||
      customerPhone.includes(searchTerm);
    const matchesFilter =
      filterType === 'all' ||
      (filterType === 'active' && customer.IsActive) ||
      (filterType === 'inactive' && !customer.IsActive) ||
      (filterType === 'individual' && (customer.CustomerType === 'Individual' || customer.Type === 'Individual')) ||
      (filterType === 'business' && (customer.CustomerType === 'Business' || customer.Type === 'Business'));
    return matchesSearch && matchesFilter;
  });

  return (
    <Container maxWidth="xl" sx={{ mt: 2, mb: 4 }}>
      <Box sx={{ mb: 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
          <Box>
            <Typography
              variant="h4"
              component="h1"
              sx={{
                fontWeight: 700,
                color: 'primary.main',
                mb: 0.5
              }}
            >
              {t("customers")}
            </Typography>
            <Typography variant="body1" color="text.secondary">
              {t("customer_management_desc")}
            </Typography>
          </Box>
          <Button
            variant="contained"
            startIcon={<PersonAddIcon />}
            onClick={() => navigate('/customers/add')}
            sx={{
              borderRadius: 2,
              px: 3,
              py: 1.5,
              textTransform: 'none',
              fontWeight: 600,
              boxShadow: 2
            }}
          >
            {t("add_customer")}
          </Button>
        </Box>
      </Box>

      <Paper
        elevation={2}
        sx={{
          p: 3,
          mb: 3,
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider'
        }}
      >
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              placeholder={t("search_customers_placeholder")}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon color="action" />
                  </InputAdornment>
                ),
                endAdornment: searchTerm && (
                  <InputAdornment position="end">
                    <IconButton
                      size="small"
                      onClick={() => setSearchTerm('')}
                      edge="end"
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                )
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 2,
                  bgcolor: 'background.paper'
                }
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <FormControl fullWidth>
              <InputLabel>{t("filter")}</InputLabel>
              <Select
                value={filterType}
                label={t("filter")}
                onChange={(e) => setFilterType(e.target.value)}
                sx={{ borderRadius: 2 }}
              >
                <MenuItem value="all">{t("all_customers")}</MenuItem>
                <MenuItem value="active">{t("active_only")}</MenuItem>
                <MenuItem value="inactive">{t("inactive_only")}</MenuItem>
                <MenuItem value="individual">{t("individual")}</MenuItem>
                <MenuItem value="business">{t("business")}</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={3}>
            <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
              <Chip
                label={`${filteredCustomers.length} ${t("customers")}`}
                color="primary"
                variant="outlined"
                icon={<FilterListIcon />}
              />
            </Box>
          </Grid>
        </Grid>
      </Paper>

      <Paper
        elevation={2}
        sx={{
          borderRadius: 2,
          border: '1px solid',
          borderColor: 'divider',
          overflow: 'hidden'
        }}
      >
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
            <CircularProgress />
            <Typography variant="body1" sx={{ ml: 2 }}>
              {t("loading_customers")}
            </Typography>
          </Box>
        ) : filteredCustomers.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <PeopleIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
            <Typography variant="h6" color="text.secondary" gutterBottom>
              {t("no_customers_found")}
            </Typography>
            <Typography variant="body2" color="text.disabled">
              {searchTerm || filterType !== 'all'
                ? t("adjust_search_filter")
                : t("get_started_add_customer")}
            </Typography>
            {!searchTerm && filterType === 'all' && (
              <Button
                variant="contained"
                startIcon={<PersonAddIcon />}
                onClick={() => navigate('/customers/add')}
                sx={{ mt: 2 }}
              >
                {t("add_first_customer")}
              </Button>
            )}
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>{t("customer")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>{t("contact")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>{t("total_invoices")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>{t("total_invoice_amount")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>{t("total_paid_amount")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>{t("due_amount")}</TableCell>
                    <TableCell sx={{ fontWeight: 600, py: 2 }}>{t("status")}</TableCell>
                    <TableCell align="center" sx={{ fontWeight: 600, py: 2 }}>{t("actions")}</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredCustomers
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((customer) => (
                      <TableRow
                        key={customer.Id}
                        hover
                        sx={{
                          '&:hover': { bgcolor: 'action.hover' },
                          borderBottom: '1px solid',
                          borderColor: 'divider'
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              sx={{
                                bgcolor: 'primary.main',
                                width: 40,
                                height: 40,
                                fontSize: '1rem'
                              }}
                            >
                              {(customer.CustomerName || customer.Name || t("customer")).charAt(0)}
                            </Avatar>
                            <Box>
                              <Typography variant="body1" sx={{ fontWeight: 600 }}>
                                {customer.CustomerName || customer.Name || t("unknown_customer")}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                              VAT: {customer.VATNumber}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                              <EmailIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {customer.Email || t("no_email")}
                              </Typography>
                            </Box>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                              <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                              <Typography variant="body2">
                                {customer.PhoneNumber || customer.Phone || t("no_phone")}
                              </Typography>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" align="center" sx={{ fontWeight: 600 }}>
                            {customer.TotalInvoices || 0}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" align="center" sx={{ fontWeight: 600, color: 'primary.main' }}>
                            €{(customer.TotalInvoiceAmount || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" align="center" sx={{ fontWeight: 600, color: 'success.main' }}>
                            €{(customer.TotalPaidAmount || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" align="center" sx={{ fontWeight: 600, color: 'warning.main' }}>
                            €{(customer.TotalDueAmount || 0).toLocaleString()}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip
                            label={customer.IsActive ? t("active") : t("inactive")}
                            size="small"
                            color={customer.IsActive ? 'success' : 'error'}
                            sx={{ fontWeight: 500 }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                            <Tooltip title={t("view_details")}>
                              <IconButton
                                size="small"
                                color="info"
                                onClick={(event) => handleView(customer, event)}
                              >
                                <ViewIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("edit")}>
                              <IconButton
                                size="small"
                                color="primary"
                                onClick={(event) => handleEdit(customer, event)}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={t("delete")}>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={(event) => handleDelete(customer.Id, event)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        </TableCell>
                      </TableRow>
                    ))}
                </TableBody>
              </Table>
            </TableContainer>
            <TablePagination
              component="div"
              count={filteredCustomers.length}
              page={page}
              onPageChange={(event, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(event) => {
                setRowsPerPage(parseInt(event.target.value, 10));
                setPage(0);
              }}
              rowsPerPageOptions={[5, 10, 25, 50]}
              sx={{
                borderTop: '1px solid',
                borderColor: 'divider',
                bgcolor: 'grey.50'
              }}
            />
          </>
        )}
      </Paper>
      <Fab
        color="primary"
        aria-label={t("add_customer")}
        onClick={() => navigate('/customers/add')}
        sx={{
          position: 'fixed',
          bottom: 24,
          right: 24,
          boxShadow: 4
        }}
      >
        <AddIcon />
      </Fab>
    </Container>
  );
};

export default CustomersListPage;